import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../lib/auth.js';

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    void navigate({ to: '/login' });
  };

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() ?? '?';

  // Show first part of email only on mobile (before @), or use name
  const displayName = user?.name ?? user?.email?.split('@')[0] ?? '—';

  return (
    <header className="h-16 bg-zinc-900 border-b border-zinc-800 flex items-center px-4 md:px-6 shrink-0 gap-3">
      {/* Hamburger — mobile only */}
      <button
        onClick={onMenuClick}
        className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg text-slate-400 hover:text-slate-100 hover:bg-zinc-800 transition-colors shrink-0"
        aria-label="Open menu"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Tenant info — hidden on mobile, shown on md+ */}
      <div className="hidden md:flex items-center gap-2 flex-1 min-w-0">
        <span className="text-sm text-zinc-500">Tenant:</span>
        <span className="text-sm font-medium text-slate-200 truncate">{user?.tenantId ?? '—'}</span>
      </div>

      {/* AOS logo text on mobile (fills center) */}
      <div className="flex-1 md:hidden" />

      {/* User dropdown */}
      <div className="relative shrink-0">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity min-h-[44px] px-1"
        >
          <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-xs font-bold text-white shrink-0">
            {initials}
          </div>
          <span className="hidden sm:block text-sm text-slate-300 max-w-[120px] truncate">{displayName}</span>
        </button>

        {open && (
          <div className="absolute right-0 top-full mt-2 w-52 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-50 py-1">
            <div className="px-4 py-3 border-b border-zinc-700">
              <p className="text-xs text-zinc-400">Signed in as</p>
              <p className="text-sm font-medium text-slate-200 truncate">{user?.email}</p>
              <p className="text-xs text-zinc-500 capitalize">{user?.role}</p>
            </div>
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-zinc-700 transition-colors min-h-[44px] flex items-center"
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
